import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useAbsenceReportingSubmissions } from '../hooks/useAbsenceReporting';
import { useDirectory } from '../hooks/useDirectory';
import { STATUS_CONFIRM_LABEL, categoryBySlug } from './absenceReportingCategories';

const FSM_URL = process.env.REACT_APP_FSM_URL;

function fmtDate(d) {
  return d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) : '—';
}

// Deep-links to FSM's form with whatever it can answer for the reporter
// already: who the employee is (Q1), their department/area (Q2/Q3, carried
// over from their last submission), and which of the three branches (Q4) —
// same field names FSM's own absenceReportingFlow.js nodes use, since the
// form reads these straight into its answers state as query params.
// FSM's own /absence-reporting now serves its native copy of this same
// category/list flow (see nano-fibre-fsm's AbsenceReportingDashboard.jsx) —
// the actual submission form moved to /absence-reporting/form there.
function buildFsmHref(row, category) {
  if (!FSM_URL) return undefined;
  const params = new URLSearchParams();
  if (row.employee_email) params.set('employeeEmail', row.employee_email);
  if (row.department_area) params.set('departmentArea', row.department_area);
  if (row.area) params.set('area', row.area);
  params.set('reportType', category.reportType);
  return `${FSM_URL}/absence-reporting/form?${params.toString()}`;
}

// The per-category detail page — everyone reported under this category,
// with a per-row confirm toggle standing in for the old click-to-select
// row. Each row confirms independently (a manager can be working through
// several people at once), which is why this is a Set keyed by submission
// id rather than a single "selected" value.
function AbsenceReportingCategory() {
  const { category: slug } = useParams();
  const category = categoryBySlug(slug);
  const { data: submissions, isLoading, error } = useAbsenceReportingSubmissions();
  const { data: directory } = useDirectory();
  const [confirmedIds, setConfirmedIds] = useState(() => new Set());

  const rows = useMemo(
    () => (submissions || []).filter((r) => r.report_type === category?.reportType),
    [submissions, category]
  );

  // employee_email is all absence_reporting_submissions has for the person
  // being reported on — they may never have used FSM themselves, so there's
  // no login-derived name to fall back on. The MS 365 display name has to
  // come from the company directory, matched by email.
  const namesByEmail = useMemo(() => {
    const map = new Map();
    for (const p of directory?.people || []) {
      if (p.email) map.set(p.email.toLowerCase(), p.display_name);
    }
    return map;
  }, [directory]);

  function toggleConfirmed(id) {
    setConfirmedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (!category) {
    return (
      <div className="p-4 sm:p-8">
        <p className="text-sm text-rose-700">Unknown category.</p>
        <Link to="/absence-reporting" className="text-sm font-medium text-brand hover:text-brand-dark">
          Back to categories
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <Link
        to="/absence-reporting"
        className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Back to categories
      </Link>

      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">{category.label}</h1>
        <p className="mt-1 text-sm text-gray-500">Confirm a person is still in this state to act on them.</p>
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading submissions...</p>}
      {error && (
        <p className="text-sm text-rose-700">
          Could not load submissions from FSM: {error.response?.data?.error || error.message}
        </p>
      )}

      {!isLoading && !error && (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {['Employee', 'Reported by', 'Date', 'Confirm', 'Action'].map((h) => (
                  <th key={h} className="whitespace-nowrap px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const confirmed = confirmedIds.has(r.id);
                const canProceed = confirmed && Boolean(FSM_URL);
                const displayName = r.employee_email ? namesByEmail.get(r.employee_email.toLowerCase()) : null;
                return (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium text-gray-900">
                      {displayName || r.employee_email || '—'}
                      {displayName && r.employee_email && (
                        <span className="block text-xs font-normal text-gray-400">{r.employee_email}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{r.engineer_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-500">{fmtDate(r.submitted_at)}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={confirmed}
                        aria-label={`Confirm ${r.employee_email || 'employee'} is ${STATUS_CONFIRM_LABEL[category.reportType]}`}
                        onClick={() => toggleConfirmed(r.id)}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                          confirmed ? 'bg-brand' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                            confirmed ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      {canProceed ? (
                        <a
                          href={buildFsmHref(r, category)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:border-brand hover:text-brand"
                        >
                          <category.icon className="h-3.5 w-3.5" />
                          {category.label}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">Confirm to proceed</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400">
                    No {category.label.toLowerCase()} submissions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AbsenceReportingCategory;
